import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import {
  Key,
  Bell,
  Moon,
  Sun,
  Globe,
  Save,
  Trash2,
  ChevronRight,
  CreditCard,
  AlertTriangle,
  Check,
  Lock,
  Shield as ShieldIcon,
  Mail,
  MessageSquare,
  Star
} from 'lucide-react';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import VerificationBanner from '../../components/VerificationBanner';
import { ThemeContext } from '../../context/ThemeContext';
import { getCurrentUser } from '../../services/auth';
import api from '../../services/api';
import { checkReviewEligibility } from '../../services/reviews';

const SettingsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { darkMode, toggleDarkMode } = useContext(ThemeContext);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteStep, setDeleteStep] = useState(1);
  const [deletePassword, setDeletePassword] = useState('');
  const [deletePasswordError, setDeletePasswordError] = useState('');
  const [hasExistingReview, setHasExistingReview] = useState(false);
  const [reviewEligibility, setReviewEligibility] = useState(null);
  
  // Get tab from URL query params
  const queryParams = new URLSearchParams(location.search);
  const tabParam = queryParams.get('tab');
  const validTabs = ['general', 'notifications', 'security', 'payment'];
  const [activeTab, setActiveTab] = useState(validTabs.includes(tabParam) ? tabParam : 'general');
  
  const [settings, setSettings] = useState({
    email_notifications: true,
    product_updates: true,
    security_alerts: true,
    marketing_emails: false,
    language: 'en',
    timezone: 'UTC',
    two_factor_auth: false,
    payment_methods: [
      {
        id: 'card_1',
        type: 'card',
        brand: 'visa',
        last4: '4242',
        expMonth: 12,
        expYear: 2025,
        isDefault: true
      }
    ]
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        const user = getCurrentUser();

        if (!user) {
          navigate('/login');
          return;
        }

        // Try to get the latest settings data from the API
        try {
          const response = await api.get('/User/settings');
          setSettings(response.data);
        } catch (error) {
          // Fallback to default settings if API fails
          console.error('Failed to fetch user settings:', error);
          // Keep the default settings
        }

        // Check if user has an existing review
        try {
          const eligibility = await checkReviewEligibility();
          setReviewEligibility(eligibility);
          setHasExistingReview(eligibility.existingReview && eligibility.existingReview.submittedAt !== null);
        } catch (error) {
          // If API fails, assume no review
          console.error('Failed to check review eligibility:', error);
        }
      } catch (err) {
        setError('Failed to load settings. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, [navigate]);

  const handleToggle = (setting) => {
    setSettings({
      ...settings,
      [setting]: !settings[setting]
    });
    
    // Clear success message when settings are changed
    if (success) {
      setSuccess(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSettings({
      ...settings,
      [name]: value
    });
    
    // Clear success message when settings are changed
    if (success) {
      setSuccess(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      setError(null);
      
      // Make API call to update settings
      await api.put('/User/settings', settings);
      
      setSuccess(true);
      
      // Hide success message after 3 seconds
      setTimeout(() => {
        setSuccess(false);
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleAddPaymentMethod = () => {
    alert(`In a real implementation, this would open Stripe Elements to add a payment method:
    
1. Create a payment method with Stripe.js:
   const { paymentMethod, error } = await stripe.createPaymentMethod({
     type: 'card',
     card: cardElement,
     billing_details: { name, email }
   });

2. Send to backend:
   await api.post('/payments/methods', { paymentMethodId: paymentMethod.id });
    `);
  };

  const setDefaultPaymentMethod = (methodId) => {
    // Update the default payment method
    const updatedMethods = settings.payment_methods.map(method => ({
      ...method,
      isDefault: method.id === methodId
    }));
    
    setSettings({
      ...settings,
      payment_methods: updatedMethods
    });
    
    if (success) {
      setSuccess(false);
    }
  };

  const removePaymentMethod = (methodId) => {
    if (window.confirm('Are you sure you want to remove this payment method?')) {
      const updatedMethods = settings.payment_methods.filter(
        method => method.id !== methodId
      );
      
      setSettings({
        ...settings,
        payment_methods: updatedMethods
      });
      
      if (success) {
        setSuccess(false);
      }
    }
  };

  const handleDeleteAccount = () => {
    setShowDeleteModal(true);
    setDeleteStep(1);
    setDeletePassword('');
    setDeletePasswordError('');
  };
  
  const handlePasswordConfirm = () => {
    // Validate password (in real implementation, verify with backend)
    if (deletePassword.length < 8) {
      setDeletePasswordError('Please enter your password to confirm');
      return;
    }
    setDeletePasswordError('');
    setDeleteStep(2);
  };
  
  const handleFinalDelete = async () => {
    try {
      // In real implementation, make API call to delete account
      // await api.delete('/User/account');
      alert('Account deletion would be processed here');
      setShowDeleteModal(false);
      // Redirect to homepage or login
      // navigate('/');
    } catch (err) {
      setError('Failed to delete account. Please try again.');
      setShowDeleteModal(false);
    }
  };
  
  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteStep(1);
    setDeletePassword('');
    setDeletePasswordError('');
  };

  const handleChangePassword = () => {
    // Navigate to password change page or show modal
    navigate('/account/change-password');
  };

  const timezones = [
    { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
    { value: 'America/New_York', label: 'Eastern Time (US & Canada)' },
    { value: 'America/Chicago', label: 'Central Time (US & Canada)' },
    { value: 'America/Denver', label: 'Mountain Time (US & Canada)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (US & Canada)' },
    { value: 'Europe/London', label: 'London' },
    { value: 'Europe/Paris', label: 'Paris' },
    { value: 'Asia/Tokyo', label: 'Tokyo' },
    { value: 'Australia/Sydney', label: 'Sydney' }
  ];

  const languages = [
    { value: 'en', label: 'English' },
    { value: 'es', label: 'Spanish' },
    { value: 'fr', label: 'French' },
    { value: 'de', label: 'German' },
    { value: 'zh', label: 'Chinese (Simplified)' },
    { value: 'ja', label: 'Japanese' }
  ];

  const renderTabContent = () => {
    switch(activeTab) {
      case 'general':
        return (
          <div className="space-y-8">
            <div>
              <label htmlFor="language" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Language
              </label>
              <select
                id="language"
                name="language"
                value={settings.language}
                onChange={handleChange}
                className="block w-full bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md py-2.5 px-4 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-gray-900 dark:text-white transition-colors h-11 appearance-none bg-no-repeat bg-right"
                style={{
                  backgroundImage: "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236B7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3E%3C/svg%3E\")",
                  backgroundSize: "1.5em 1.5em",
                  backgroundPosition: "right 0.5rem center",
                  paddingRight: "2.5rem"
                }}
              >
                {languages.map((language) => (
                  <option key={language.value} value={language.value}>
                    {language.label}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label htmlFor="timezone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Timezone
              </label>
              <select
                id="timezone"
                name="timezone"
                value={settings.timezone}
                onChange={handleChange}
                className="block w-full bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md py-2.5 px-4 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-gray-900 dark:text-white transition-colors h-11 appearance-none bg-no-repeat bg-right"
                style={{
                  backgroundImage: "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236B7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3E%3C/svg%3E\")",
                  backgroundSize: "1.5em 1.5em",
                  backgroundPosition: "right 0.5rem center",
                  paddingRight: "2.5rem"
                }}
              >
                {timezones.map((timezone) => (
                  <option key={timezone.value} value={timezone.value}>
                    {timezone.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Product Review</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {hasExistingReview ? 'Manage your review' : 'Share your experience with Torque'}
                  </p>
                </div>
                {reviewEligibility?.isEligible || hasExistingReview ? (
                  <Link
                    to="/account/review"
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                  >
                    <Star className="h-4 w-4 mr-1.5" />
                    {hasExistingReview ? 'Edit Review' : 'Leave a Review'}
                  </Link>
                ) : (
                  <button
                    disabled
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 cursor-not-allowed opacity-60"
                  >
                    <Star className="h-4 w-4 mr-1.5" />
                    Leave a Review
                  </button>
                )}
              </div>
              {reviewEligibility && !reviewEligibility.isEligible && !hasExistingReview && (
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400 italic">
                  {reviewEligibility.reason || 'You may leave a review after 14 days of using our product!'}
                </p>
              )}
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Theme</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {darkMode ? 'Currently using dark theme' : 'Currently using light theme'}
                </p>
              </div>
              <button
                onClick={toggleDarkMode}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                {darkMode ? (
                  <>
                    <Sun className="h-4 w-4 mr-1.5" />
                    Light Mode
                  </>
                ) : (
                  <>
                    <Moon className="h-4 w-4 mr-1.5" />
                    Dark Mode
                  </>
                )}
              </button>
            </div>
          </div>
        );
        
      case 'notifications':
        return (
          <div>
            <div className="flex items-center justify-between py-5 border-b border-gray-200 dark:border-gray-700">
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Email Notifications</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Receive notifications via email</p>
              </div>
              <div className="relative inline-block w-12 mr-2 align-middle select-none">
                <input
                  type="checkbox"
                  id="email_notifications"
                  checked={settings.email_notifications}
                  onChange={() => handleToggle('email_notifications')}
                  className="sr-only"
                />
                <label
                  htmlFor="email_notifications"
                  className={`flex items-center w-12 h-6 rounded-full cursor-pointer transition-colors duration-200 ease-in-out ${
                    settings.email_notifications ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <span
                    className={`inline-block bg-white dark:bg-white rounded-full h-5 w-5 transform transition-transform duration-200 ease-in-out ${
                      settings.email_notifications ? 'translate-x-[26px]' : 'translate-x-0.5'
                    }`}
                  />
                </label>
              </div>
            </div>
            
            <div className="flex items-center justify-between py-5 border-b border-gray-200 dark:border-gray-700">
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Product Updates</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Receive updates about product features</p>
              </div>
              <div className="relative inline-block w-12 mr-2 align-middle select-none">
                <input
                  type="checkbox"
                  id="product_updates"
                  checked={settings.product_updates}
                  onChange={() => handleToggle('product_updates')}
                  className="sr-only"
                />
                <label
                  htmlFor="product_updates"
                  className={`flex items-center w-12 h-6 rounded-full cursor-pointer transition-colors duration-200 ease-in-out ${
                    settings.product_updates ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <span
                    className={`inline-block bg-white dark:bg-white rounded-full h-5 w-5 transform transition-transform duration-200 ease-in-out ${
                      settings.product_updates ? 'translate-x-[26px]' : 'translate-x-0.5'
                    }`}
                  />
                </label>
              </div>
            </div>
            
            <div className="flex items-center justify-between py-5 border-b border-gray-200 dark:border-gray-700">
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Security Alerts</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Receive alerts about security issues</p>
              </div>
              <div className="relative inline-block w-12 mr-2 align-middle select-none">
                <input
                  type="checkbox"
                  id="security_alerts"
                  checked={settings.security_alerts}
                  onChange={() => handleToggle('security_alerts')}
                  className="sr-only"
                />
                <label
                  htmlFor="security_alerts"
                  className={`flex items-center w-12 h-6 rounded-full cursor-pointer transition-colors duration-200 ease-in-out ${
                    settings.security_alerts ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <span
                    className={`inline-block bg-white dark:bg-white rounded-full h-5 w-5 transform transition-transform duration-200 ease-in-out ${
                      settings.security_alerts ? 'translate-x-[26px]' : 'translate-x-0.5'
                    }`}
                  />
                </label>
              </div>
            </div>
            
            <div className="flex items-center justify-between py-5">
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Marketing Emails</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Receive marketing and promotional emails</p>
              </div>
              <div className="relative inline-block w-12 mr-2 align-middle select-none">
                <input
                  type="checkbox"
                  id="marketing_emails"
                  checked={settings.marketing_emails}
                  onChange={() => handleToggle('marketing_emails')}
                  className="sr-only"
                />
                <label
                  htmlFor="marketing_emails"
                  className={`flex items-center w-12 h-6 rounded-full cursor-pointer transition-colors duration-200 ease-in-out ${
                    settings.marketing_emails ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <span
                    className={`inline-block bg-white dark:bg-white rounded-full h-5 w-5 transform transition-transform duration-200 ease-in-out ${
                      settings.marketing_emails ? 'translate-x-[26px]' : 'translate-x-0.5'
                    }`}
                  />
                </label>
              </div>
            </div>
          </div>
        );
        
      case 'security':
        return (
          <div className="space-y-6">
            <div className="py-5 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Password</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Change your account password</p>
              </div>
              <button
                onClick={handleChangePassword}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                <Key className="h-4 w-4 mr-1.5" />
                Change Password
              </button>
              </div>
            </div>
            
            <div className="py-5 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center">
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Two-Factor Authentication</h3>
                    <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400 rounded-full">
                      BETA
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Add an extra layer of security to your account</p>
                </div>
                <div className="flex items-center">
                  {settings.two_factor_auth ? (
                    <span className="mr-4 text-xs px-2 py-1 rounded-full bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400 flex items-center">
                      <Check className="w-3 h-3 mr-1" />
                      Enabled
                    </span>
                  ) : (
                    <span className="mr-4 text-xs px-2 py-1 rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400 flex items-center">
                      <AlertTriangle className="w-3 h-3 mr-1" />
                      Disabled
                    </span>
                  )}
                  <div className="relative inline-block w-12 mr-2 align-middle select-none">
                    <input
                      type="checkbox"
                      id="two_factor_auth"
                      checked={settings.two_factor_auth}
                      onChange={() => handleToggle('two_factor_auth')}
                      className="sr-only"
                    />
                    <label
                      htmlFor="two_factor_auth"
                      className={`flex items-center w-12 h-6 rounded-full cursor-pointer transition-colors duration-200 ease-in-out ${
                        settings.two_factor_auth ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                      }`}
                    >
                      <span
                        className={`inline-block bg-white dark:bg-white rounded-full h-5 w-5 transform transition-transform duration-200 ease-in-out ${
                          settings.two_factor_auth ? 'translate-x-[26px]' : 'translate-x-0.5'
                        }`}
                      />
                    </label>
                  </div>
                </div>
              </div>
              
              {/* Expanded 2FA Options */}
              {settings.two_factor_auth && (
                <div className="mt-6 space-y-4 pl-4 border-l-2 border-blue-200 dark:border-blue-800">
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                    <div className="flex items-start">
                      <ShieldIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 mr-3" />
                      <div className="flex-1">
                        <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Authenticator App</h4>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                          Use an authenticator app like Google Authenticator or Authy to generate time-based codes
                        </p>
                        <button className="inline-flex items-center px-3 py-1.5 border border-blue-300 dark:border-blue-600 rounded-md shadow-sm text-xs font-medium text-blue-700 dark:text-blue-300 bg-white dark:bg-gray-800 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors">
                          Configure Authenticator
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
                    <div className="flex items-start">
                      <MessageSquare className="w-5 h-5 text-gray-600 dark:text-gray-400 mt-0.5 mr-3" />
                      <div className="flex-1">
                        <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">SMS Authentication</h4>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                          Receive verification codes via SMS to your registered phone number
                        </p>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500 dark:text-gray-400">Phone: +1 ••• ••• 4242</span>
                          <button className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors">
                            Update Phone
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
                    <div className="flex items-start">
                      <Mail className="w-5 h-5 text-gray-600 dark:text-gray-400 mt-0.5 mr-3" />
                      <div className="flex-1">
                        <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Email Authentication</h4>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                          Receive verification codes via email to your registered address
                        </p>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500 dark:text-gray-400">Email: user@example.com</span>
                          <button className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors">
                            Configure Email 2FA
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
                    <div className="flex items-start">
                      <Key className="w-5 h-5 text-gray-600 dark:text-gray-400 mt-0.5 mr-3" />
                      <div className="flex-1">
                        <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Backup Codes</h4>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                          Generate one-time use backup codes for emergency access
                        </p>
                        <div className="flex items-center space-x-3">
                          <button className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors">
                            Generate Codes
                          </button>
                          <span className="text-xs text-gray-500 dark:text-gray-400">No backup codes generated</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                    <div className="flex items-start">
                      <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 mr-2 flex-shrink-0" />
                      <p className="text-xs text-amber-800 dark:text-amber-300">
                        <strong>Important:</strong> Make sure to save your backup codes in a secure location. You'll need them if you lose access to your authentication device.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            <div className="py-5">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Delete Account</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Permanently delete your account and all data
                  </p>
                </div>
                <button
                  onClick={handleDeleteAccount}
                  className="inline-flex items-center px-3 py-1.5 border border-red-300 dark:border-red-700 rounded-md text-sm font-medium text-red-600 dark:text-red-400 bg-white dark:bg-gray-800 hover:bg-red-50 dark:hover:bg-red-900/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                >
                  <Trash2 className="h-4 w-4 mr-1.5" />
                  Delete Account
                </button>
              </div>
            </div>
          </div>
        );
        
      case 'payment':
        return (
          <div className="space-y-6">
            <div className="mb-4 flex justify-between items-center">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Payment Methods</h3>
              <button 
                onClick={handleAddPaymentMethod}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                <span className="text-lg mr-1">+</span> Add New Payment Method
              </button>
            </div>
            
            {settings.payment_methods.length > 0 ? (
              <div className="space-y-3">
                {settings.payment_methods.map((method) => (
                  <div key={method.id} className="flex items-center justify-between py-3 border-b border-gray-200 dark:border-gray-700 last:border-0">
                    <div className="flex items-center">
                      <div className="w-10 h-10 rounded-md bg-gray-100 dark:bg-gray-700 flex items-center justify-center mr-3">
                        {method.brand === 'visa' ? (
                          <span className="text-blue-600 dark:text-blue-400 font-bold text-sm">VISA</span>
                        ) : method.brand === 'mastercard' ? (
                          <span className="text-red-600 dark:text-red-400 font-bold text-sm">MC</span>
                        ) : (
                          <CreditCard className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center">
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            •••• {method.last4}
                          </p>
                          {method.isDefault && (
                            <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400">
                              Default
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Expires {method.expMonth}/{method.expYear}
                        </p>
                      </div>
                    </div>
                    <div className="flex">
                      {!method.isDefault && (
                        <button 
                          onClick={() => setDefaultPaymentMethod(method.id)}
                          className="text-sm text-blue-600 dark:text-blue-400 mr-3 hover:underline"
                        >
                          Set Default
                        </button>
                      )}
                      <button 
                        onClick={() => removePaymentMethod(method.id)}
                        className="text-sm text-red-600 dark:text-red-400 hover:underline"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-4 text-center bg-gray-50 dark:bg-gray-800 rounded-md">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  You don't have any payment methods added yet.
                </p>
              </div>
            )}
            
            <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Billing Address</h3>
              <button 
                onClick={() => setActiveTab('payment')}
                className="mt-2 inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                Edit Billing Address
              </button>
            </div>
            
            <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Billing History</h3>
              <Link 
                to="/account/billing-history" 
                className="mt-2 inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                View Billing History
              </Link>
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50 dark:bg-gray-900 color-transition">
      <Navbar />
      <VerificationBanner />

      <div className="flex-grow container mx-auto px-4 sm:px-5 lg:px-7 py-10 mt-16">
        {/* Account navigation breadcrumbs */}
        <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 mb-6">
          <Link to="/" className="hover:text-blue-600 dark:hover:text-blue-400">Home</Link>
          <ChevronRight className="h-4 w-4 mx-2" />
          <Link to="/account/settings" className="text-blue-600 dark:text-blue-400 font-medium">Settings</Link>
        </div>

        {/* Account navigation tabs */}
        <div className="flex border-b border-gray-200 dark:border-gray-700 mb-8">
          <Link 
            to="/account/profile" 
            className="px-4 py-2 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-b-2 hover:border-gray-300 dark:hover:border-gray-600"
          >
            Profile
          </Link>
          <Link 
            to="/account/subscription" 
            className="px-4 py-2 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-b-2 hover:border-gray-300 dark:hover:border-gray-600"
          >
            Subscription
          </Link>
          <Link 
            to="/account/settings" 
            className="px-4 py-2 text-sm font-medium border-b-2 border-blue-600 dark:border-blue-400 text-blue-600 dark:text-blue-400"
            aria-current="page"
          >
            Settings
          </Link>
          {/* <Link
            to="/account/referrals"
            className="px-4 py-2 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-b-2 hover:border-gray-300 dark:hover:border-gray-600"
          >
            Referrals
          </Link> */}
        </div>
        
        <div className="max-w-4xl mx-auto">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 dark:border-blue-400"></div>
            </div>
          ) : (
            <div className="bg-gray-100 dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden color-transition">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h1 className="text-lg font-medium text-gray-900 dark:text-white">Account Settings</h1>
              </div>
              
              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 text-red-700 dark:text-red-400 m-6 px-4 py-3 rounded-md flex items-start">
                  <div className="flex-shrink-0 mt-0.5">
                    <AlertTriangle className="h-5 w-5" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm">{error}</p>
                  </div>
                </div>
              )}
              
              {success && (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-300 dark:border-green-700 text-green-700 dark:text-green-400 m-6 px-4 py-3 rounded-md flex items-start">
                  <div className="flex-shrink-0 mt-0.5">
                    <Check className="h-5 w-5" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm">Settings saved successfully!</p>
                  </div>
                </div>
              )}
              
              <div className="flex flex-col sm:flex-row">
                {/* Sidebar */}
                <div className="w-full sm:w-48 border-b sm:border-b-0 sm:border-r border-gray-200 dark:border-gray-700">
                  <div className="px-6 py-5">
                    <nav className="space-y-1">
                      <button
                        onClick={() => {
                          setActiveTab('general');
                          navigate('/account/settings?tab=general', { replace: true });
                        }}
                        className={`flex items-center px-3 py-2 text-sm font-medium rounded-md w-full text-left ${
                          activeTab === 'general'
                            ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      >
                        <Globe className={`mr-3 h-5 w-5 ${
                          activeTab === 'general'
                            ? 'text-blue-500 dark:text-blue-400'
                            : 'text-gray-500 dark:text-gray-400'
                        }`} />
                        General
                      </button>
                      
                      <button
                        onClick={() => {
                          setActiveTab('notifications');
                          navigate('/account/settings?tab=notifications', { replace: true });
                        }}
                        className={`flex items-center px-3 py-2 text-sm font-medium rounded-md w-full text-left ${
                          activeTab === 'notifications'
                            ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      >
                        <Bell className={`mr-3 h-5 w-5 ${
                          activeTab === 'notifications'
                            ? 'text-blue-500 dark:text-blue-400'
                            : 'text-gray-500 dark:text-gray-400'
                        }`} />
                        Notifications
                      </button>
                      
                      <button
                        onClick={() => {
                          setActiveTab('security');
                          navigate('/account/settings?tab=security', { replace: true });
                        }}
                        className={`flex items-center px-3 py-2 text-sm font-medium rounded-md w-full text-left ${
                          activeTab === 'security'
                            ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      >
                        <Lock className={`mr-3 h-5 w-5 ${
                          activeTab === 'security'
                            ? 'text-blue-500 dark:text-blue-400'
                            : 'text-gray-500 dark:text-gray-400'
                        }`} />
                        Security
                      </button>
                      
                      <button
                        onClick={() => {
                          setActiveTab('payment');
                          navigate('/account/settings?tab=payment', { replace: true });
                        }}
                        className={`flex items-center px-3 py-2 text-sm font-medium rounded-md w-full text-left ${
                          activeTab === 'payment'
                            ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      >
                        <CreditCard className={`mr-3 h-5 w-5 ${
                          activeTab === 'payment'
                            ? 'text-blue-500 dark:text-blue-400'
                            : 'text-gray-500 dark:text-gray-400'
                        }`} />
                        Payment
                      </button>
                    </nav>
                  </div>
                </div>
              
                {/* Main content */}
                <div className="flex-1 p-6">
                  {renderTabContent()}
                  
                  {activeTab !== 'security' && (
                    <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700 flex justify-end">
                      <button
                        onClick={handleSaveSettings}
                        disabled={saving}
                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors disabled:opacity-70"
                      >
                        {saving ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save className="h-4 w-4 mr-2" />
                            Save Changes
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full p-6 relative transform transition-all">
            {deleteStep === 1 ? (
              // Step 1: Password Confirmation
              <>
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                    <AlertTriangle className="w-8 h-8 text-red-600 dark:text-red-400" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Delete Account</h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    This action cannot be undone. Please enter your password to continue.
                  </p>
                </div>
                
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Enter your password to confirm
                  </label>
                  <input
                    type="password"
                    value={deletePassword}
                    onChange={(e) => {
                      setDeletePassword(e.target.value);
                      setDeletePasswordError('');
                    }}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="Enter password"
                    autoFocus
                  />
                  {deletePasswordError && (
                    <p className="mt-2 text-sm text-red-600 dark:text-red-400">{deletePasswordError}</p>
                  )}
                </div>
                
                <div className="flex gap-3">
                  <button
                    onClick={closeDeleteModal}
                    className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handlePasswordConfirm}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Continue
                  </button>
                </div>
              </>
            ) : (
              // Step 2: Final Confirmation
              <>
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Trash2 className="w-8 h-8 text-red-600 dark:text-red-400" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                    Are you absolutely sure?
                  </h3>
                  <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4 text-left border border-red-200 dark:border-red-800">
                    <p className="text-sm text-red-800 dark:text-red-300 mb-3">
                      <strong>This will permanently delete:</strong>
                    </p>
                    <ul className="text-sm text-red-700 dark:text-red-400 space-y-1 list-disc list-inside">
                      <li>Your account and profile information</li>
                      <li>All your saved data and preferences</li>
                      <li>Your subscription and billing history</li>
                      <li>Access to all Torque services</li>
                    </ul>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-4">
                    Type <span className="font-mono font-bold text-red-600 dark:text-red-400">DELETE</span> to confirm
                  </p>
                </div>
                
                <div className="mb-6">
                  <input
                    type="text"
                    placeholder="Type DELETE to confirm"
                    onChange={(e) => {
                      if (e.target.value === 'DELETE') {
                        e.target.classList.add('border-red-500');
                      } else {
                        e.target.classList.remove('border-red-500');
                      }
                    }}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent dark:bg-gray-700 dark:text-white font-mono"
                    autoFocus
                  />
                </div>
                
                <div className="flex gap-3">
                  <button
                    onClick={() => setDeleteStep(1)}
                    className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    Go Back
                  </button>
                  <button
                    onClick={handleFinalDelete}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Account
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      
      <Footer />
    </div>
  );
};

export default SettingsPage;